import React, { useState, useEffect } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import TrocarSenhaModal from '../../../components/vision/TrocarSenhaModal'

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [mostrarTrocaSenha, setMostrarTrocaSenha] = useState(false)
  const [usuarioId, setUsuarioId] = useState('')
  const [usuarioNome, setUsuarioNome] = useState('')
  const { signIn, isAuthenticated, user } = useAuth()

  useEffect(() => {
    if (isAuthenticated) {
      window.location.href = '/vision/dashboard'
    }
  }, [isAuthenticated])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { error, primeiroAcesso } = await signIn(email, password)
    
    if (error) {
      setError('Email ou senha incorretos')
      setLoading(false)
    } else if (primeiroAcesso) {
      // Primeiro acesso - mostrar modal de troca de senha
      try {
        const response = await fetch(`/api/gestao/usuarios?email=${encodeURIComponent(email)}`)
        if (response.ok) {
          const userData = await response.json()
          setUsuarioId(userData.id)
          setUsuarioNome(userData.nome_completo || userData.nome || 'Usuário')
          setMostrarTrocaSenha(true)
          setLoading(false)
        } else {
          setError('Erro ao carregar dados do usuário')
          setLoading(false)
        }
      } catch (err) {
        console.error('Erro ao buscar dados do usuário:', err)
        setError('Erro ao processar login')
        setLoading(false)
      }
    } else {
      // Login normal - redirecionar para dashboard
      window.location.href = '/vision/dashboard'
    }
  }

  const handleTrocaSenhaSuccess = () => {
    // Após trocar senha com sucesso, redirecionar para dashboard
    setMostrarTrocaSenha(false)
    window.location.href = '/vision/dashboard'
  }

  const goToHome = () => {
    window.location.href = '/'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-900 to-red-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20digital%20technology%20background%20with%20AI%20neural%20networks%2C%20data%20visualization%2C%20holographic%20interfaces%2C%20vibrant%20orange%20and%20red%20gradients%2C%20minimalist%20tech%20aesthetic%2C%20professional%20business%20environment%2C%20warm%20orange%20lighting&width=1920&height=1080&seq=login-bg-orange&orientation=landscape')`
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-transparent" />
      
      {/* Back to Home Button */}
      <button
        onClick={goToHome}
        className="absolute top-6 left-6 text-white/80 hover:text-white transition-all duration-300 flex items-center space-x-2 group z-10"
      >
        <svg className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-medium">Voltar ao site</span>
      </button>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://static.readdy.ai/image/60d079db6e314477a5615b4d3efd8435/1aa70460cb4235142615a402493549d1.png" 
            alt="Vision 360" 
            className="h-16 w-auto mx-auto mb-6 opacity-90"
          />
          <h1 className="text-3xl font-bold text-white mb-2">Vision 360</h1>
          <p className="text-gray-300">Inteligência de Performance</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-200">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300"
                placeholder="seu@email.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 relative overflow-hidden group ${
                loading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-2xl hover:shadow-orange-500/25 hover:-translate-y-1'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <span className="relative z-10 flex items-center justify-center">
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Entrando...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Entrar no Sistema
                  </>
                )}
              </span>
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Acesso restrito aos gestores da M4 Digital
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-400 text-sm">
          <p>© 2026 M4 Digital - Transformando dados em crescimento real.</p>
        </div>
      </div>

      {/* Modal de Troca de Senha (Primeiro Acesso) */}
      {mostrarTrocaSenha && (
        <TrocarSenhaModal
          usuarioId={usuarioId}
          usuarioNome={usuarioNome}
          onSuccess={handleTrocaSenhaSuccess}
        />
      )}
    </div>
  )
}

export default LoginPage
